import { IAccount, IAccountMailbox } from './account.models';
import type { IConversation, IConversationDetails } from './conversation.models';
import { ITimelineMessage } from './timeline.models';

// ─── AI Draft ─────────────────────────────────────────────────────────────────

export const AiDraftProcessingStatus = {
  COMPLETED: 'completed',
  OPERATOR_REQUIRED: 'operator_required',
  REJECTED: 'rejected',
  FAILED: 'failed',
} as const;
export type AiDraftProcessingStatus = (typeof AiDraftProcessingStatus)[keyof typeof AiDraftProcessingStatus];

export interface IDraftPlaceholder {
  id: string;
  description?: string;
}

// ─── Compose Type ────────────────────────────────────────────────────────────

export const ComposeType = {
  COMPOSE: 'compose',
  REPLY: 'reply',
  REPLY_ALL: 'reply-all',
  FORWARD: 'forward',
} as const;
export type ComposeType = (typeof ComposeType)[keyof typeof ComposeType];

// ─── Shared email fields ─────────────────────────────────────────────────────

export interface IComposeFields {
  from?: IAccount | IAccountMailbox;
  to?: string;
  subject?: string;
  cc?: string;
  bcc?: string;
  body?: string;
}

// ─── Params passed when opening a compose panel ──────────────────────────────

export interface IComposeParams extends IComposeFields {
  type?: ComposeType;
  draftId?: number | null;
  reply_to_conversation?: IConversation | IConversationDetails;
  reply_to_message?: ITimelineMessage;
  attachments?: IComposePanelAttachment[];
  /** When true, the panel auto-triggers AI draft generation as soon as its editor is ready. */
  autoAiDraft?: boolean;
}

// ─── Compose panel attachment (file for upload; name/size for display; id after server upload) ───────

export interface IComposePanelAttachment {
  file?: File;
  name: string;
  size: number;
  id?: number;
}

// ─── Per-panel state ─────────────────────────────────────────────────────────

export interface IComposePanel {
  id: string;
  type: ComposeType;
  from: IAccount | IAccountMailbox | null;
  to: string;
  subject: string;
  cc: string;
  bcc: string;
  body: string;
  zIndex: number;
  minimized?: boolean;
  sending?: boolean;
  draftId?: number | null;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  reply_to_conversation?: IConversation;
  reply_to_message?: ITimelineMessage;
  quoteAppended?: boolean;
  /** When true, the panel auto-triggers AI draft generation once its editor is ready (consumed once on open). */
  autoAiDraft?: boolean;
  aiDraft?: {
    placeholders: IDraftPlaceholder[];
    reasoning?: string;
    processingStatus?: AiDraftProcessingStatus;
    processingErrors?: string | null;
    dismissed?: boolean;
  };
  attachments?: IComposePanelAttachment[];
}
